import { PredictionService } from '@/app/core/services/prediction/prediction.service';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EnovaAlertsComponent } from '@/app/core/utils/enova-alerts/enova-alerts.component';
import {
  GetDataDesviacionVSModelo,
  GetDataErrorAbsolute,
  GetDataPrediccionVsRealGraph,
} from './desviation-prediction.action';
import { IBaseResponse } from '@/app/core/interfaces/prediction.interface';
import { from } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface IPredictionDesviationModel {
  predictionVsReal: {
    labels: string[];
    datasets: any[];
  };
  desviationModel: {
    labels: string[];
    datasets: any[];
  };
  errorAbsolute: IBaseResponse;
  selectedDesviationGeneralFilters: any[];
  selectedDesviationClient: string;
}

@State<IPredictionDesviationModel>({
  name: 'desviationPrediction',
  defaults: {
    predictionVsReal: {
      labels: [],
      datasets: [],
    },
    desviationModel: {
      labels: [],
      datasets: [],
    },
    errorAbsolute: { column: [], data: [] },
    selectedDesviationGeneralFilters: [],
    selectedDesviationClient: '',
  },
})
@Injectable()
export class DesviationPredictionState {
  @Selector() static GetPredictionVSRealData(
    state: IPredictionDesviationModel
  ) {
    return state?.predictionVsReal;
  }

  @Selector() static GetErrorAbsolute(state: IPredictionDesviationModel) {
    return state.errorAbsolute;
  }

  @Selector() static getSelectedDesviationGeneralFilters(
    state: IPredictionDesviationModel
  ): any {
    return state?.selectedDesviationGeneralFilters
      ? state.selectedDesviationGeneralFilters
      : [];
  }

  @Selector() static getSelectedDesviationClient(
    state: IPredictionDesviationModel
  ): string {
    return state?.selectedDesviationClient
      ? state.selectedDesviationClient
      : '';
  }

  @Selector() static GetDesviationModel(state: IPredictionDesviationModel) {
    return state?.desviationModel ? state.desviationModel : {};
  }

  constructor(
    private predictionService: PredictionService,
    public dialog: MatDialog
  ) {}

  @Action(GetDataPrediccionVsRealGraph)
  GetPredictionDesviationDataAction(
    ctx: StateContext<IPredictionDesviationModel>,
    action: GetDataPrediccionVsRealGraph
  ) {
    const { startDate, endDate, filters } = action.payload;

    return from(
      this.predictionService.getPrediccion(
        startDate.replace(/\//g, '-'),
        endDate.replace(/\//g, '-'),
        filters
      )
    ).pipe(
      tap({
        next: (response: any) => {
          // Verificar si hay un error en la respuesta
          if (response?.message === 'ApplicationError' || response?.details) {
            this.dialog.open(EnovaAlertsComponent, {
              width: 'auto',
              data: {
                type: 'error',
                text:
                  response.details ||
                  response.message ||
                  'Ha ocurrido un error al obtener los datos',
                hiddeCancelBtn: true,
              },
            });
            return;
          }

          // Verificar si la respuesta tiene la estructura esperada
          if (
            !response ||
            !response.data ||
            typeof response.data !== 'object'
          ) {
            this.dialog.open(EnovaAlertsComponent, {
              width: 'auto',
              data: {
                type: 'error',
                text: 'Ha ocurrido un error al obtener los datos',
                hiddeCancelBtn: true,
              },
            });
            return;
          }

          const labels = new Set<string>();
          for (const category in response.data) {
            if (
              response.data.hasOwnProperty(category) &&
              Array.isArray(response.data[category])
            ) {
              for (const item of response.data[category]) {
                if (item && item.fecha) {
                  labels.add(item.fecha);
                }
              }
            }
          }

          const uniqueLabels = Array.from(labels);
          const sortedDates = uniqueLabels.sort((a, b) => {
            return new Date(a).getTime() - new Date(b).getTime();
          });

          const editedResponse = Object.keys(response.data).reduce(
            (acc: any, key: string) => {
              if (Array.isArray(response.data[key])) {
                acc[key] = response.data[key].sort((a: any, b: any) => {
                  return (
                    new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
                  );
                });
              }
              return acc;
            },
            {}
          );

          const hasData = Object.keys(editedResponse).some(
            key => editedResponse[key] && editedResponse[key].length > 0
          );

          if (
            hasData &&
            editedResponse.udc &&
            editedResponse.pudc &&
            editedResponse.rcdtac &&
            editedResponse.rcudc &&
            editedResponse.mpp &&
            editedResponse.mbtu
          ) {
            const datasets = [
              {
                label: 'UDC',
                data: editedResponse.udc.map((item: any) => item.prediccion),
                borderColor: '#ED7D31',
              },
              {
                label: 'PUDC',
                data: editedResponse.pudc.map((item: any) => item.prediccion),
                borderColor: '#A5A5A5',
                borderDash: [10, 5],
              },
              {
                label: 'RCDTAC',
                data: editedResponse.rcdtac.map((item: any) => item.prediccion),
                borderColor: '#FFC000',
                borderDash: [10, 5],
              },
              {
                label: 'RCUDC',
                data: editedResponse.rcudc.map((item: any) => item.prediccion),
                borderColor: '#5B9BD5',
                borderDash: [10, 5],
              },
              {
                label: 'MPP',
                data: editedResponse.mpp.map((item: any) => item.prediccion),
                borderColor: '##F00',
              },
              {
                label: 'MBTU',
                data: editedResponse.mbtu.map((item: any) => item.prediccion),
                borderColor: '#000000',
              },
            ];

            ctx.patchState({
              predictionVsReal: {
                labels: sortedDates,
                datasets: datasets,
              },
            });
          }
        },
        error: error => {
          const errorMessage =
            error?.error?.details ||
            error?.details ||
            error?.message ||
            'Ha ocurrido un error al obtener los datos';
          this.dialog.open(EnovaAlertsComponent, {
            width: 'auto',
            data: {
              type: 'error',
              text: errorMessage,
              hiddeCancelBtn: true,
            },
          });
        },
      })
    );
  }

  @Action(GetDataErrorAbsolute)
  GetDataErrorAbsoluteAction(
    ctx: StateContext<IPredictionDesviationModel>,
    action: GetDataErrorAbsolute
  ) {
    const { startDate, endDate, filters } = action.payload;

    return from(
      this.predictionService.getErrorAbsolutoMedio(
        startDate.replace(/\//g, '-'),
        endDate.replace(/\//g, '-'),
        filters
      )
    ).pipe(
      tap({
        next: (response: any) => {
          // Verificar si hay un error en la respuesta
          if (response?.message === 'ApplicationError' || response?.details) {
            this.dialog.open(EnovaAlertsComponent, {
              width: 'auto',
              data: {
                type: 'error',
                text:
                  response.details ||
                  response.message ||
                  'Ha ocurrido un error al obtener los datos',
                hiddeCancelBtn: true,
              },
            });
            return;
          }

          // Verificar si la respuesta tiene la estructura esperada
          if (
            !response ||
            !response.column ||
            !response.data ||
            !Array.isArray(response.data)
          ) {
            this.dialog.open(EnovaAlertsComponent, {
              width: 'auto',
              data: {
                type: 'error',
                text: 'Ha ocurrido un error al obtener los datos',
                hiddeCancelBtn: true,
              },
            });
            return;
          }

          const { column, data } = response;
          const roundedData = data
            .filter((item: any) => item != null && item.fecha)
            .map((item: any) => ({
              ...item,
              udc: item.udc ? parseFloat(item.udc.toFixed(3)) : 0,
              pudc: item.pudc ? parseFloat(item.pudc.toFixed(3)) : 0,
              rcdtac: item.rcdtac ? parseFloat(item.rcdtac.toFixed(3)) : 0,
              rcudc: item.rcudc ? parseFloat(item.rcudc.toFixed(3)) : 0,
            }))
            .sort(
              (a: any, b: any) =>
                new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
            );

          ctx.patchState({
            errorAbsolute: {
              column,
              data: roundedData,
            },
          });
        },
        error: error => {
          const errorMessage =
            error?.error?.details ||
            error?.details ||
            error?.message ||
            'Ha ocurrido un error al obtener los datos';
          this.dialog.open(EnovaAlertsComponent, {
            width: 'auto',
            data: {
              type: 'error',
              text: errorMessage,
              hiddeCancelBtn: true,
            },
          });
        },
      })
    );
  }

  @Action(GetDataDesviacionVSModelo)
  GetDataDesviacionVSModeloAction(
    ctx: StateContext<IPredictionDesviationModel>,
    action: GetDataDesviacionVSModelo
  ) {
    const { startDate, endDate, filters } = action.payload;

    return from(
      this.predictionService.getDesviacionStandar(
        startDate.replace(/\//g, '-'),
        endDate.replace(/\//g, '-'),
        filters
      )
    ).pipe(
      tap({
        next: (response: any) => {
          if (response?.message === 'ApplicationError' || response?.details) {
            console.error('Error en respuesta:', response);
            this.dialog.open(EnovaAlertsComponent, {
              width: 'auto',
              data: {
                type: 'error',
                text:
                  response.details ||
                  response.message ||
                  'Ha ocurrido un error al obtener los datos',
                hiddeCancelBtn: true,
              },
            });
            return;
          }

          if (
            !response ||
            !response.data ||
            typeof response.data !== 'object'
          ) {
            console.error('Estructura de respuesta inválida:', response);
            this.dialog.open(EnovaAlertsComponent, {
              width: 'auto',
              data: {
                type: 'error',
                text: 'Ha ocurrido un error al obtener los datos',
                hiddeCancelBtn: true,
              },
            });
            return;
          }

          const labels = new Set<string>();

          for (const category in response.data) {
            if (
              response.data.hasOwnProperty(category) &&
              Array.isArray(response.data[category])
            ) {
              for (const item of response.data[category]) {
                if (item && item.fecha) {
                  labels.add(item.fecha);
                }
              }
            }
          }

          const uniqueLabels = Array.from(labels);
          const sortedDates = uniqueLabels.sort((a, b) => {
            return new Date(a).getTime() - new Date(b).getTime();
          });

          const editedResponse = Object.keys(response.data).reduce(
            (acc: any, key: string) => {
              const modifiedKey =
                key === 'GAS NATURAL VEHÍCULAR'
                  ? 'GASNATURALVEHÍCULAR'
                  : key === 'GRUPO GI'
                    ? 'GRUPOGI'
                    : key === 'AUTOGENERACIÓN'
                      ? 'AUTOGENERACION'
                      : key;
              if (Array.isArray(response.data[key])) {
                acc[modifiedKey] = response.data[key].sort((a: any, b: any) => {
                  return (
                    new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
                  );
                });
              }
              return acc;
            },
            {}
          );

          const hasData = Object.keys(editedResponse).some(
            key => editedResponse[key] && editedResponse[key].length > 0
          );

          const requiredCategories = [
            'REGULADO',
            'INDUSTRIAL',
            'GASNATURALVEHÍCULAR',
            'GRUPOGI',
            'AUTOGENERACION',
          ];
          const availableCategories = requiredCategories.filter(
            cat => editedResponse[cat]
          );

          if (hasData && availableCategories.length > 0) {
            const datasets: any[] = [];

            const colorMap: { [key: string]: string } = {
              REGULADO: '#2400FF',
              INDUSTRIAL: '#F50C0C',
              GASNATURALVEHÍCULAR: '#dadbdc',
              GRUPOGI: '#ffc000',
              AUTOGENERACION: '#5B9BD5',
            };

            const allAvailableCategories = Object.keys(editedResponse).filter(
              key =>
                editedResponse[key] &&
                Array.isArray(editedResponse[key]) &&
                editedResponse[key].length > 0
            );

            allAvailableCategories.forEach(category => {
              const data = editedResponse[category].map(
                (item: any) => item.porcentajeDesviacion
              );
              const color = colorMap[category] || this.getRandomColor();

              datasets.push({
                label: category,
                data: data,
                borderColor: color,
              });
            });

            ctx.patchState({
              desviationModel: {
                labels: sortedDates,
                datasets: datasets,
              },
            });
          } else {
            console.warn(
              'No se cumplen las condiciones para crear datasets. hasData:',
              hasData,
              'Categorías disponibles:',
              availableCategories
            );
          }
        },
        error: error => {
          const errorMessage =
            error?.error?.details ||
            error?.details ||
            error?.message ||
            'Ha ocurrido un error al obtener los datos';
          this.dialog.open(EnovaAlertsComponent, {
            width: 'auto',
            data: {
              type: 'error',
              text: errorMessage,
              hiddeCancelBtn: true,
            },
          });
        },
      })
    );
  }

  private getRandomColor(): string {
    const colors = [
      '#FF6B6B',
      '#4ECDC4',
      '#45B7D1',
      '#96CEB4',
      '#FFEAA7',
      '#DDA0DD',
      '#98D8C8',
      '#F7DC6F',
      '#BB8FCE',
      '#85C1E9',
      '#F8C471',
      '#82E0AA',
      '#F1948A',
      '#85C1E9',
      '#D7BDE2',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }
}
